import {
  EVENTS,
  SessionType,
  SESSIONS_ARRAY,
  type EventItem,
  type Session,
} from "./core";
import { formatLocation } from "@/util/formatters";
import { buildSessions, sessionSlug } from "@/util/talks";
import { buildSearchHaystack } from "@/util/string";
import { groupSessionsByOccurrence } from "@/util/talk-occurrences";
import { getUtcMidnightTimestamp } from "@/util/date";

export const ALL_SESSIONS = buildSessions(EVENTS);
export const ALL_SESSION_OCCURRENCES = groupSessionsByOccurrence(ALL_SESSIONS);

// Pre-calculate indices for all sessions (0 to N-1)
// This avoids creating this array on every client-side render when filter is "All"
export const ALL_SESSION_INDICES = ALL_SESSIONS.map((_, i) => i);

// Pre-calculate indices for each session type
// This avoids O(N) iteration on the client when switching filters
export const SESSION_INDICES_BY_TYPE = (() => {
  const map = new Map<SessionType, number[]>();
  for (const type of Object.values(SessionType)) {
    map.set(type, []);
  }
  ALL_SESSIONS.forEach((session, index) => {
    map.get(session.type)?.push(index);
  });
  return map;
})();

// Split events into "Archived" (definitely past) and "Recent" (active/upcoming).
// This static split allows us to optimize the "Past" table by skipping filtering for older events,
// while ensuring "Recent" contains enough future data to support 100+ years of time-travel testing.
// Cutoff: Jan 1, 2026.
const ARCHIVE_CUTOFF_YEAR = 2026;
const yearNum = (yearString: string) => {
  const parsed = Number(yearString);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const ARCHIVED_SESSION_OCCURRENCES = ALL_SESSION_OCCURRENCES.filter(
  (occurrence) => yearNum(occurrence.year) < ARCHIVE_CUTOFF_YEAR,
);

export const RECENT_SESSION_OCCURRENCES = ALL_SESSION_OCCURRENCES.filter(
  (occurrence) => yearNum(occurrence.year) >= ARCHIVE_CUTOFF_YEAR,
);

export const RECENT_SESSION_OCCURRENCES_SORTED_BY_DATE_ASC = [
  ...RECENT_SESSION_OCCURRENCES,
].sort((a, b) => a.startDateTimestamp - b.startDateTimestamp);

export const ARCHIVED_YEAR_COUNTS = (() => {
  const yearCounts = new Map<string, number>();
  ARCHIVED_SESSION_OCCURRENCES.forEach((event) =>
    yearCounts.set(event.year, (yearCounts.get(event.year) || 0) + 1),
  );
  return Array.from(yearCounts.entries());
})();

export const SESSION_COUNTS = (() => {
  const getCount = (types: SessionType[]) =>
    types.reduce(
      (sum, type) => sum + (SESSION_INDICES_BY_TYPE.get(type)?.length ?? 0),
      0,
    );

  return {
    all: ALL_SESSIONS.length,
    talks: getCount([
      SessionType.Talk,
      SessionType.PanelDiscussion,
      SessionType.LightningTalk,
    ]),
    workshops: getCount([SessionType.Workshop]),
    mentorings: getCount([SessionType.Mentoring]),
  };
})();

export const SESSION_TYPES = Object.values(SessionType) as SessionType[];

export const SESSIONS_HAY = ALL_SESSIONS.map((session) =>
  buildSearchHaystack(
    [
      session.event,
      session.title,
      session.location ?? "Online",
      session.type,
    ].join(" "),
  ),
);

function groupEventsByKey(
  keySelector: (event: EventItem) => string | null | undefined,
) {
  const map = new Map<string, EventItem[]>();
  for (const event of EVENTS) {
    const key = keySelector(event);
    if (key) {
      const existing = map.get(key) ?? [];
      existing.push(event);
      map.set(key, existing);
    }
  }
  // Sort events by date descending for each group
  for (const events of map.values()) {
    // Optimization: Use string comparison for ISO dates to avoid expensive Date object creation
    events.sort((a, b) => b.startDate.localeCompare(a.startDate));
  }
  return map;
}

export const LOCATION_TO_EVENTS = groupEventsByKey((event) =>
  formatLocation(event.location),
);

export const COUNTRY_TO_EVENTS = groupEventsByKey(
  (event) => event.location?.country,
);

const baseSessionSlug = (title: string) =>
  sessionSlug(title.replace(/ - \d{4} Edition$/, ""));

export type SessionWithStats = Session & {
  popularity: number;
  lastDate: number;
  _base: string;
  description?: string | null;
  languageCode?: string | null;
};

export const BASE_SESSIONS: SessionWithStats[] = (() => {
  const sessionsByBase = new Map<string, { session: Session; year: number }>();
  for (const session of SESSIONS_ARRAY) {
    const titleYearMatch = session.title.match(/ - (\d{4}) Edition$/);
    const year = titleYearMatch ? parseInt(titleYearMatch[1], 10) : 0;
    const base = baseSessionSlug(session.title);
    const cur = sessionsByBase.get(base);
    if (!cur || year > cur.year) {
      sessionsByBase.set(base, { session: session, year });
    }
  }
  const counts = new Map<string, number>();
  const last = new Map<string, number>();
  for (const session of ALL_SESSIONS) {
    const base = baseSessionSlug(session.title);
    counts.set(base, (counts.get(base) ?? 0) + 1);
    // Use getUtcMidnightTimestamp for stable, timezone-invariant sorting
    const date = getUtcMidnightTimestamp(session.date);
    const prev = last.get(base) ?? 0;
    if (date > prev) last.set(base, date);
  }
  return Array.from(sessionsByBase.entries()).map(([base, { session }]) => ({
    ...session,
    _base: base,
    popularity: counts.get(base) ?? 0,
    lastDate: last.get(base) ?? 0,
  }));
})();
